import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  adminFormInputClasses,
  adminFormTextareaClasses,
  adminFormSelectClasses,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_TOOLBAR_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';

const CATEGORY_OPTIONS = ['Abuse', 'Illegal', 'Safety', 'Spam', 'Compliance'];
const SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS = ['Active', 'Testing', 'Disabled'];
const APPLIES_TO_OPTIONS = ['Message', 'Review', 'Post', 'Quote', 'Document', 'Profile'];
const TRIGGER_TYPE_OPTIONS = ['Keyword', 'Pattern', 'Behavioural'];
const SENSITIVITY_OPTIONS = ['Low', 'Moderate', 'High'];
const FLAG_REASON_OPTIONS = ['Harassment', 'Scam', 'Abuse', 'Spam', 'Compliance', 'Copyright', 'Policy', 'Other'];
const DEFAULT_ENFORCEMENT_OPTIONS = [
  'No Action',
  'Remove Content',
  'Remove Content + Warning',
  'Messaging Restricted',
  'Account Suspended',
  'Escalate to Compliance',
  'Escalate to Security & Fraud'
];

const buildDummyRule = (ruleId) => {
  if (ruleId === 'new') {
    return null;
  }
  
  return {
    id: ruleId || 'RULE-ABUSE-001',
    ruleId: ruleId || 'RULE-ABUSE-001',
    name: 'Abusive Language — Direct Insults',
    category: 'Abuse',
    severity: 'High',
    appliesTo: ['Message', 'Review', 'Post'],
    status: 'Active',
    triggerType: 'Keyword',
    keywords: ['idiot', 'fraudster', 'kill yourself', 'stupid', 'moron'],
    sensitivity: 'Moderate',
    pattern: '',
    behaviouralRule: '',
    primaryFlagReason: 'Harassment',
    additionalFlagReasons: ['Abuse'],
    defaultEnforcement: 'Remove Content + Warning',
    severityOverrides: {
      high: 'Messaging Restricted',
      critical: 'Escalate to Compliance'
    },
    history: [
      { timestamp: '15 Nov 2025, 10:30 am', event: 'Rule created', actor: 'Admin: J.S.' },
      { timestamp: '18 Nov 2025, 02:15 pm', event: 'Keywords updated', actor: 'Admin: M.K.' },
      { timestamp: '20 Nov 2025, 09:45 am', event: 'Severity changed from Medium to High', actor: 'Admin: A.R.' }
    ]
  };
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
};

const getEmptyFormData = () => ({
  name: '',
  category: '',
  severity: 'Medium',
  appliesTo: [],
  status: 'Active',
  triggerType: 'Keyword',
  keywords: '',
  sensitivity: 'Moderate',
  pattern: '',
  behaviouralRule: '',
  primaryFlagReason: '',
  additionalFlagReasons: [],
  defaultEnforcement: '',
  severityOverrides: {
    high: '',
    critical: ''
  }
});

const ModerationRuleEditor = () => {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = ruleId === 'new';
  const duplicateFrom = location.state?.duplicateFrom;

  // Initialize loading state - false for new rules without duplicate
  const isNewRuleOnMount = ruleId === 'new' && !location.state?.duplicateFrom;
  const [loading, setLoading] = useState(!isNewRuleOnMount);
  const [rule, setRule] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [additionalFlagReasonInput, setAdditionalFlagReasonInput] = useState('');

  // Reset form immediately when creating a new rule - useLayoutEffect runs synchronously
  useLayoutEffect(() => {
    // Check if this is a new rule without duplicate
    const isNewRule = ruleId === 'new';
    const hasDuplicate = !!location.state?.duplicateFrom;
    
    if (isNewRule && !hasDuplicate) {
      // Immediately and synchronously reset all state for new rule
      setRule(null);
      setFormData({
        name: '',
        category: '',
        severity: 'Medium',
        appliesTo: [],
        status: 'Active',
        triggerType: 'Keyword',
        keywords: '',
        sensitivity: 'Moderate',
        pattern: '',
        behaviouralRule: '',
        primaryFlagReason: '',
        additionalFlagReasons: [],
        defaultEnforcement: '',
        severityOverrides: {
          high: '',
          critical: ''
        },
        systemFlag: 'Yes'
      });
      setAdditionalFlagReasonInput('');
      setLoading(false);
    }
  }, [ruleId, location.state?.duplicateFrom]);

  useEffect(() => {
    // Skip if it's a new rule without duplicate
    if (ruleId === 'new' && !location.state?.duplicateFrom) {
      return;
    }

    const fetchRule = async () => {
      setLoading(true);
      try {
        let ruleData = null;
        
        if (ruleId === 'new') {
          const duplicateFromState = location.state?.duplicateFrom;
          if (duplicateFromState) {
            // Duplicate mode - use the duplicated rule data
            await new Promise(resolve => setTimeout(resolve, 300));
            ruleData = { ...duplicateFromState, id: 'new', ruleId: 'new' };
          } else {
            // Should not reach here, but just in case
            setLoading(false);
            return;
          }
        } else {
          // Edit mode - fetch existing rule
          await new Promise(resolve => setTimeout(resolve, 500));
          // Check if rule data was passed via location.state (only for edit mode)
          const passedRule = location.state?.rule;
          if (passedRule && ruleId !== 'new') {
            ruleData = passedRule;
          } else {
            ruleData = buildDummyRule(ruleId);
          }
        }
        
        setRule(ruleData);
        
        if (ruleData) {
          // Extract primary flag reason and additional flag reasons
          let primaryFlagReason = ruleData.primaryFlagReason || ruleData.flagReason || '';
          let additionalFlagReasons = ruleData.additionalFlagReasons || [];
          
          setFormData({
            name: ruleData.name || '',
            category: ruleData.category || '',
            severity: ruleData.severity || 'Medium',
            appliesTo: ruleData.appliesTo || [],
            status: ruleData.status || 'Active',
            triggerType: ruleData.triggerType || 'Keyword',
            keywords: Array.isArray(ruleData.keywords) ? ruleData.keywords.join(', ') : (ruleData.keywords || ''),
            sensitivity: ruleData.sensitivity || 'Moderate',
            pattern: ruleData.pattern || '',
            behaviouralRule: ruleData.behaviouralRule || '',
            primaryFlagReason: primaryFlagReason,
            additionalFlagReasons: additionalFlagReasons,
            defaultEnforcement: ruleData.defaultEnforcement || ruleData.enforcement || '',
            severityOverrides: ruleData.severityOverrides || { high: '', critical: '' },
            systemFlag: ruleData.systemFlag || 'Yes'
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching rule:', error);
        setLoading(false);
      }
    };
    
    fetchRule();
  }, [ruleId, isNew, duplicateFrom, location.state]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleArrayFieldAdd = (field, value = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value]
    }));
  };

  const handleArrayFieldRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleAppliesToToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      appliesTo: prev.appliesTo.includes(type)
        ? prev.appliesTo.filter(t => t !== type)
        : [...prev.appliesTo, type]
    }));
  };

  const handleAdditionalFlagReasonAdd = () => {
    if (additionalFlagReasonInput.trim()) {
      setFormData(prev => ({
        ...prev,
        additionalFlagReasons: [...prev.additionalFlagReasons, additionalFlagReasonInput.trim()]
      }));
      setAdditionalFlagReasonInput('');
    }
  };

  const handleAdditionalFlagReasonRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalFlagReasons: prev.additionalFlagReasons.filter((_, i) => i !== index)
    }));
  };

  const handleSeverityOverrideChange = (severity, value) => {
    setFormData(prev => ({
      ...prev,
      severityOverrides: {
        ...prev.severityOverrides,
        [severity]: value
      }
    }));
  };

  const handleSave = () => {
    // TODO: Implement save API call
    console.log('Saving rule:', formData);
    navigate('/admin/moderation/rules');
  };

  const handleCancel = () => {
    navigate('/admin/moderation/rules');
  };

  const handleSaveAsNew = () => {
    // TODO: Implement save as new API call
    console.log('Saving as new rule:', formData);
    navigate('/admin/moderation/rules');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading rule editor...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  const displayRuleId = isNew ? 'Auto-generated' : (rule?.ruleId || '—');

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 w-full min-w-0">
              <AdminPageTitle className="!mb-0">
                {isNew ? 'Create New Rule' : `Edit Rule: ${rule?.ruleId || ruleId}`}
              </AdminPageTitle>
            </div>
          </div>

          {/* A. Basic Info */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Basic Info</AdminPanelTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Rule Name <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                    placeholder="e.g., Abusive Language — Direct Insults"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Rule Category <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                    required
                  >
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Severity <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <select
                    value={formData.severity}
                    onChange={(e) => handleInputChange('severity', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                    required
                  >
                    {SEVERITY_OPTIONS.map(severity => (
                      <option key={severity} value={severity}>{severity}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Status
                  </AdminDetailFieldLabel>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <AdminDetailFieldLabel>
                    Applies To <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <div className="flex flex-wrap gap-2">
                    {APPLIES_TO_OPTIONS.map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleAppliesToToggle(type)}
                        className={`px-3 py-1.5 rounded border text-xs transition-colors ${
                          formData.appliesTo.includes(type)
                            ? 'bg-primaryblue text-white border-primaryblue'
                            : 'bg-white text-offblack border-lightgray hover:border-primaryblue'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Rule ID
                  </AdminDetailFieldLabel>
                  <input
                    type="text"
                    value={displayRuleId}
                    disabled
                    className={`${adminFormInputClasses(true)} w-full`}
                  />
                  <AdminSupportingText>
                    {isNew ? 'Auto-generated ID' : 'System-generated ID'}
                  </AdminSupportingText>
                </div>
              </div>
            </div>
          </DashboardMainContainer>

          {/* B. Trigger Settings */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Trigger Settings (Detection Logic)</AdminPanelTitle>
              
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Trigger Type <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <select
                  value={formData.triggerType}
                  onChange={(e) => handleInputChange('triggerType', e.target.value)}
                  className={`${adminFormInputClasses(false)} w-full`}
                  required
                >
                  {TRIGGER_TYPE_OPTIONS.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Keyword List */}
              {formData.triggerType === 'Keyword' && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Keyword List <span className="text-alertred">*</span>
                    </AdminDetailFieldLabel>
                    <textarea
                      value={formData.keywords}
                      onChange={(e) => handleInputChange('keywords', e.target.value)}
                      className={adminFormTextareaClasses()}
                      placeholder='Enter keywords separated by commas, e.g., "idiot", "fraudster", "kill yourself"'
                      required
                    />
                    <AdminSupportingText>
                      Enter keywords separated by commas
                    </AdminSupportingText>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      Sensitivity
                    </AdminDetailFieldLabel>
                    <select
                      value={formData.sensitivity}
                      onChange={(e) => handleInputChange('sensitivity', e.target.value)}
                      className={`${adminFormInputClasses(false)} w-full`}
                    >
                      {SENSITIVITY_OPTIONS.map(sens => (
                        <option key={sens} value={sens}>{sens}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Pattern / Regex */}
              {formData.triggerType === 'Pattern / Regex' && (
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Pattern <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <input
                    type="text"
                    value={formData.pattern}
                    onChange={(e) => handleInputChange('pattern', e.target.value)}
                    className={`${adminFormInputClasses()} w-full`}
                    placeholder='e.g., /send.*bank.*transfer/i'
                    required
                  />
                  <AdminSupportingText>
                    Enter regex pattern
                  </AdminSupportingText>
                </div>
              )}

              {/* Behavioural Rule */}
              {formData.triggerType === 'Behavioural' && (
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Behavioural Rule <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <input
                    type="text"
                    value={formData.behaviouralRule}
                    onChange={(e) => handleInputChange('behaviouralRule', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                    placeholder="e.g., 3 user reports within 24h"
                    required
                  />
                  <AdminSupportingText>
                    Describe the behavioural trigger condition
                  </AdminSupportingText>
                </div>
              )}

              {/* Document Rule */}
              {formData.triggerType === 'Document Rule' && (
                <div className="space-y-1">
                  <AdminDetailFieldLabel>
                    Document Rule <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <select
                    value={formData.pattern}
                    onChange={(e) => handleInputChange('pattern', e.target.value)}
                    className={`${adminFormInputClasses(false)} w-full`}
                    required
                  >
                    <option value="">Select document rule</option>
                    <option value="type_mismatch">Document type mismatch</option>
                    <option value="licence_expired">Licence expired</option>
                    <option value="invalid_format">Invalid format</option>
                    <option value="missing_required">Missing required document</option>
                  </select>
                </div>
              )}

            </div>
          </DashboardMainContainer>

          {/* C. Flag Reason Mapping */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Flag Reason Mapping</AdminPanelTitle>
              
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Primary Flag Reason <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <select
                  value={formData.primaryFlagReason}
                  onChange={(e) => handleInputChange('primaryFlagReason', e.target.value)}
                  className={`${adminFormInputClasses(false)} w-full`}
                  required
                >
                  <option value="">Select flag reason</option>
                  {FLAG_REASON_OPTIONS.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <AdminDetailFieldLabel>
                  Additional Flag Reasons (Optional)
                </AdminDetailFieldLabel>
                <div className="flex gap-2">
                  <select
                    value={additionalFlagReasonInput}
                    onChange={(e) => setAdditionalFlagReasonInput(e.target.value)}
                    className={`${adminFormInputClasses(false)} flex-1`}
                  >
                    <option value="">Select additional reason</option>
                    {FLAG_REASON_OPTIONS.filter(r => r !== formData.primaryFlagReason).map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                  <AdminFormSecondaryAction type="button" onClick={handleAdditionalFlagReasonAdd}>
                    Add
                  </AdminFormSecondaryAction>
                </div>
                {formData.additionalFlagReasons.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.additionalFlagReasons.map((reason, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-1.5 rounded border border-lightgray bg-gray-50"
                      >
                        <span className="text-xs text-offblack">{reason}</span>
                        <button
                          type="button"
                          onClick={() => handleAdditionalFlagReasonRemove(index)}
                          className="text-alertred hover:text-offblack"
                        >
                          <i className="fi fi-rr-cross text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  System Flag <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => handleInputChange('systemFlag', 'Yes')}
                    className={`px-4 py-2 rounded border text-xs transition-colors ${
                      formData.systemFlag === 'Yes'
                        ? 'bg-primaryblue text-white border-primaryblue'
                        : 'bg-white text-offblack border-lightgray hover:border-primaryblue'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('systemFlag', 'No')}
                    className={`px-4 py-2 rounded border text-xs transition-colors ${
                      formData.systemFlag === 'No'
                        ? 'bg-primaryblue text-white border-primaryblue'
                        : 'bg-white text-offblack border-lightgray hover:border-primaryblue'
                    }`}
                  >
                    No
                  </button>
                </div>
                <AdminSupportingText>
                  Indicates whether the system can create a moderation case when this rule is triggered
                </AdminSupportingText>
              </div>
            </div>
          </DashboardMainContainer>

          {/* D. Enforcement Mapping */}
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Enforcement Mapping</AdminPanelTitle>
              
              <div className="space-y-1">
                <AdminDetailFieldLabel>
                  Default Enforcement <span className="text-alertred">*</span>
                </AdminDetailFieldLabel>
                <select
                  value={formData.defaultEnforcement}
                  onChange={(e) => handleInputChange('defaultEnforcement', e.target.value)}
                  className={`${adminFormInputClasses(false)} w-full`}
                  required
                >
                  <option value="">Select enforcement</option>
                  {DEFAULT_ENFORCEMENT_OPTIONS.map(enforcement => (
                    <option key={enforcement} value={enforcement}>{enforcement}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-4 pt-2">
                <AdminDetailFieldLabel>
                  Severity Overrides (Optional)
                </AdminDetailFieldLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      If Severity = High
                    </AdminDetailFieldLabel>
                    <input
                      type="text"
                      value={formData.severityOverrides.high}
                      onChange={(e) => handleSeverityOverrideChange('high', e.target.value)}
                      className={`${adminFormInputClasses(false)} w-full`}
                      placeholder="e.g., Escalate automatically"
                    />
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>
                      If Severity = Critical
                    </AdminDetailFieldLabel>
                    <input
                      type="text"
                      value={formData.severityOverrides.critical}
                      onChange={(e) => handleSeverityOverrideChange('critical', e.target.value)}
                      className={`${adminFormInputClasses(false)} w-full`}
                      placeholder="e.g., Escalate to Legal"
                    />
                  </div>
                </div>
              </div>
            </div>
          </DashboardMainContainer>

          {/* E. Rule History (System-Generated) */}
          {rule && rule.history && rule.history.length > 0 && (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className="space-y-4">
                <AdminPanelTitle className="text-offblack mb-4">Rule History</AdminPanelTitle>
                <div className="relative">
                  <div className="space-y-0">
                    {rule.history.map((entry, index) => {
                      const isLast = index === rule.history.length - 1;
                      
                      return (
                        <div key={index} className="relative flex items-start">
                          {!isLast && (
                            <div className="absolute left-[1.45rem] top-[18px] w-px bg-lightgray" style={{ height: 'calc(100% + 1.5rem)' }}></div>
                          )}
                          
                          <div className="relative z-10 flex-shrink-0 w-12 h-8 flex items-start justify-center">
                            <div className="w-3 h-3 rounded-full bg-lightgray border-2 border-white mt-0.5 flex-shrink-0"></div>
                          </div>
                          
                          <div className="flex-1 ml-4 pb-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-offblack opacity-70">
                                  {entry.timestamp}
                                </span>
                              </div>
                            </div>
                            
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <AdminMainText className="text-offblack font-semibold">
                                  {entry.event}
                                </AdminMainText>
                              </div>
                              
                              {entry.actor && (
                                <div className="mt-1">
                                  <AdminSupportingText>
                                    By: {entry.actor}
                                  </AdminSupportingText>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </DashboardMainContainer>
          )}

          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleCancel}
                >Cancel</AdminFormSecondaryAction>
              {!isNew && (
                <AdminFormSecondaryAction type="button" onClick={handleSaveAsNew}>
                  Save as New
                </AdminFormSecondaryAction>
              )}
              <AdminHeaderAction onClick={handleSave}>
                Save Rule
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ModerationRuleEditor;

